/**
 * Schema.org structured data generators for SEO.
 * Use these to add rich snippets to pages.
 */

const BASE_URL = "https://csquaregrc.com";
const LOGO_URL = `${BASE_URL}/assets/logos/csquare_logo_horizontal.png`;

export interface FAQItem {
  question: string;
  answer: string;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface ProductOffer {
  name: string;
  price: number;
  priceCurrency?: string;
  description?: string;
}

/**
 * Organization schema for the company
 */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "cSquare GRC",
  "legalName": "cSquare GRC, Inc.",
  "alternateName": "cSquare",
  "foundingDate": "2026-08",
  "foundingLocation": {
    "@type": "Place",
    "name": "Delaware, United States",
    "address": {
      "@type": "PostalAddress",
      "addressRegion": "DE",
      "addressCountry": "US"
    }
  },
  "url": BASE_URL,
  "logo": LOGO_URL,
  "description": "The compliance platform for organizations whose obligations outgrew SOC 2. One control set carries DORA, NIS2, the EU AI Act, FedRAMP and Single Audit.",
  "parentOrganization": {
    "@type": "Organization",
    "name": "JNN Group",
    "legalName": "JNN Group, Inc.",
    "foundingDate": "2014-04",
    "url": "https://jnngroup.com"
  },
  "sameAs": [
    "https://www.linkedin.com/company/csquare-platform/",
    "https://twitter.com/cSquareAI",
    "https://www.youtube.com/@csquaregrc",
    "https://www.youtube.com/channel/UCW7LGDISH7NH2zQgLJhjz9A"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "sales",
    "email": "info@csquaregrc.com",
    "url": `${BASE_URL}/contact`
  },
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "US"
  }
};

/**
 * WebSite schema for site-wide search and identity
 */
export const webSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "cSquare GRC Compliance Platform",
  "url": BASE_URL,
  "publisher": organizationSchema
};

/**
 * SoftwareApplication schema for the platform
 */
export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "cSquare GRC Compliance Platform",
  "applicationCategory": "BusinessApplication",
  "applicationSubCategory": "Compliance Management Software",
  "operatingSystem": "Web",
  "description": "Multi-framework compliance platform for regulated organizations. One control set mapped across 60+ frameworks, on licensed regulatory data.",
  "url": BASE_URL,
  "featureList": [
    "One control set mapped across every framework you carry",
    "60+ frameworks including SOC 2, ISO 27001, GDPR, HIPAA, DORA, NIS2, EU AI Act, FedRAMP",
    "Regional and thematic regulatory content packs",
    "Continuous controls monitoring and evidence collection",
    "Controls drafted against your environment, reviewed before they count"
  ],
  "screenshot": `${BASE_URL}/og-image.png`,
  "softwareVersion": "2.0",
  "author": organizationSchema
};

/**
 * Generate FAQPage schema from FAQ items
 */
export const generateFAQSchema = (faqs: FAQItem[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Generate BreadcrumbList schema
 */
export const generateBreadcrumbSchema = (items: BreadcrumbItem[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url.startsWith("http") ? item.url : `${BASE_URL}${item.url}`
  }))
});

/**
 * Generate SoftwareApplication schema for pricing pages.
 * Note: Offer/Product schema intentionally omitted. cSquare GRC is a B2B SaaS,
 * not a merchant listing, so we avoid Google's Merchant listing requirements
 * (image, hasMerchantReturnPolicy, shippingDetails).
 */
export const generateProductSchema = (_offers: ProductOffer[]) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "cSquare GRC Compliance Platform",
  "description": "Multi-framework compliance platform for regulated enterprises.",
  "applicationCategory": "BusinessApplication",
  "applicationSubCategory": "Compliance Management Software",
  "operatingSystem": "Web",
  "url": `${BASE_URL}/pricing`,
  "brand": {
    "@type": "Brand",
    "name": "cSquare GRC"
  },
  "author": organizationSchema
});

/**
 * Generate Article schema for blog/insights posts
 */
export const generateArticleSchema = (article: {
  headline: string;
  description: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.headline,
  "description": article.description,
  "image": article.image || `${BASE_URL}/og-image.png`,
  "datePublished": article.datePublished,
  "dateModified": article.dateModified || article.datePublished,
  "author": {
    "@type": "Person",
    "name": article.author || "cSquare Research Team"
  },
  "publisher": organizationSchema
});

/**
 * Generate LocalBusiness schema for contact page
 */
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "cSquare GRC",
  "image": LOGO_URL,
  "url": BASE_URL,
  "@id": `${BASE_URL}/#business`,
  "email": "info@csquaregrc.com",
  "priceRange": "$$",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "US"
  }
};

/**
 * Combine multiple schemas for a page
 */
export const combineSchemas = (...schemas: object[]) => 
  schemas.map(schema => JSON.stringify(schema)).join('\n');

/**
 * Render structured data as a script tag string
 */
export const renderStructuredData = (schema: object): string => 
  JSON.stringify(schema);
